/**
 * Le vocabulaire affiché, à un seul endroit.
 *
 * Le produit a été écrit par des gens qui parlent de « rapprochement
 * triangulaire » et de « readiness métier ». L'exploitant, lui, demande si le
 * camion a livré ce qu'il a chargé. Les deux phrases désignent le même calcul,
 * mais une seule se lit sans traduction — et c'est celle-là qui doit être à
 * l'écran.
 *
 * Un fichier plutôt qu'une constante par page: le même mot apparaît sur
 * plusieurs écrans, et le jour où le client corrige une formulation il ne faut
 * pas qu'elle reste juste à cinq endroits sur six. Les écrans importent d'ici,
 * jamais l'inverse.
 *
 * Règle de traduction: **on ne traduit que le jargon d'ingénieur.** « Douane »,
 * « citerne », « convoi », « démurrage » sont les mots de l'exploitant lui-même
 * — les remplacer par une périphrase lui ferait chercher ce qu'on a bien pu
 * vouloir dire.
 */

export const LEXIQUE = {
  /* ---------------------------------------------------------------- */
  /* Contrôle des volumes                                              */
  /* ---------------------------------------------------------------- */

  /** Ex-« Rapprochement triangulaire ». Le calcul n'a pas changé, la question
   *  qu'il pose est simplement posée en français. */
  rapprochementVolumes: 'Le volume livré correspond-il au volume chargé ?',

  /** Ex-« Réconciliation volume ». */
  ecartVolume: 'Écart de volume',

  /** Ex-« Volumes réconciliés ». Le participe passé de « réconcilier » n'a de
   *  sens qu'en comptabilité; le volume, lui, est simplement contrôlé ou non. */
  volumesControles: 'Volumes contrôlés',

  /** Ex-« réconciliation(s) à contrôler », qui disait deux fois la même chose:
   *  la réconciliation *est* le contrôle. */
  ecartsAControler: 'Écarts à contrôler',

  /* ---------------------------------------------------------------- */
  /* Contrôles et anomalies                                            */
  /* ---------------------------------------------------------------- */

  /** Ex-« Contrôles métier ». */
  controles: 'Ce qu’il reste à faire',

  /** Ex-« Intégrité (mission) ». */
  anomaliesMission: 'Livraisons à contrôler',

  /** La même entrée dans la barre du bas d'un téléphone: cinq colonnes de
   *  75 px, donc trois mots coupés au milieu. */
  anomaliesMissionCourt: 'À contrôler',

  /** Ex-« readiness » / « readiness métier »: un état à deux valeurs, que le
   *  mot anglais présentait comme une note. */
  pret: 'Prêt',
  aCorriger: 'À corriger',

  /** Ex-« Contrat métier … ». Deux mots à jeter d'un coup: « contrat » fait
   *  penser au contrat de transport — la seule chose que ce panneau ne
   *  contient pas — et « métier » ne désigne rien pour qui exerce le métier
   *  en question. Le panneau dit ce qui permet l'action et ce qui l'empêche;
   *  autant l'écrire. */
  conditionsAction: 'Ce qui autorise ou bloque',

  /** Ex-« Blocage métier … ». */
  aDebloquer: 'À débloquer',

  /* ---------------------------------------------------------------- */
  /* Facturation                                                       */
  /* ---------------------------------------------------------------- */

  /** Ex-« BILLING_READY », un statut de base de données affiché tel quel dans
   *  les titres, les boutons et jusque dans les messages de confirmation. */
  pretAFacturer: 'Prêt à facturer',

  /** Ex-« POD » (proof of delivery). Le sigle anglais désignait le bon que le
   *  chauffeur rapporte signé — objet on ne peut plus concret, et déjà nommé
   *  en français partout ailleurs dans le produit. */
  preuveLivraison: 'Preuve de livraison',
  preuvesLivraison: 'Preuves de livraison',

  /* ---------------------------------------------------------------- */
  /* Documents                                                         */
  /* ---------------------------------------------------------------- */

  /** Ex-« OCR ». Le sigle ne dit rien de ce qui se passe; « lecture
   *  automatique » se comprend sans avoir jamais entendu parler d'OCR. */
  lectureAutomatique: 'Lecture automatique',
  lectureFiable: 'Lecture fiable',
  lectureACompleter: 'À compléter',
  /** Ex-« Échec extraction ». */
  lectureImpossible: 'Lecture impossible',

  /* ---------------------------------------------------------------- */
  /* Clients                                                           */
  /* ---------------------------------------------------------------- */

  /** Ex-« Fiche client 360 »: le « 360 » vient d'une plaquette commerciale,
   *  pas du métier. */
  ficheClient: 'Fiche client',
  /** Ex-« score risque »: c'est un niveau de risque, pas une note scolaire. */
  risque: 'Risque',
  risqueNonEvalue: 'non évalué',

  /* ---------------------------------------------------------------- */
  /* Camions                                                           */
  /* ---------------------------------------------------------------- */

  /** Ex-« Tracking », seul mot anglais resté sur la fiche camion alors que la
   *  barre latérale dit « Où sont mes camions » pour le même dispositif. */
  suiviGps: 'Suivi GPS',

  /** Le formulaire du boîtier GPS était resté en anglais. « IMEI » reste tel
   *  quel: c'est le mot imprimé sur le boîtier, celui que l'exploitant lit au
   *  téléphone à son installateur. */
  modeSuivi: 'Mode de suivi',
  fournisseurGps: 'Fournisseur',
  identifiantBoitier: 'Identifiant du boîtier',

  /** Un camion de transport d'hydrocarbures, ce sont deux pièces
   *  immatriculées: le tracteur et la citerne. Les mots de l'exploitant, tels
   *  quels — « plaque » et non « immatriculation », « châssis » et non
   *  « VIN ». */
  plaqueTracteur: 'Plaque tracteur',
  plaqueCiterne: 'Plaque citerne',
  chassisTracteur: 'Châssis tracteur',
  chassisCiterne: 'Châssis citerne',

  /** Convoi: celui qui conduit le groupe et répond au téléphone pour lui. */
  chefConvoi: 'Chef de convoi',
  retourPrevu: 'Retour prévu',

  /** Ex-« documents véhicule »: ce que l'exploitant range dans la pochette
   *  du camion. */
  papiersCamion: 'Papiers du camion',

  /* ---------------------------------------------------------------- */
  /* Menu: cinq groupes selon la vie d'un camion                       */
  /* ---------------------------------------------------------------- */

  /** On possède ou on loue des camions, on les envoie charger du carburant,
   *  on vend ce carburant, on encaisse: le menu suit cet ordre. */
  groupeCamions: 'Mes camions',
  groupeChargement: 'Envoyer au chargement',
  groupeVente: 'Vendre le carburant',
  groupeArgent: 'Encaisser et payer',
  groupeRapports: 'Rapports',

  menuCamions: 'Camions',
  menuChauffeurs: 'Chauffeurs',
  menuEntretien: 'Entretien et pannes',
  menuLocations: 'Locations de camions',
  menuConvois: 'Convois',
  menuTrajets: 'Trajets',
  menuSuivi: 'Où sont mes camions',
  menuDouane: 'Douane et incidents',
  menuClients: 'Clients',
  menuCommandes: 'Commandes',
  menuRepartition: 'Répartition des commandes',
  menuLivraisonsAControler: 'Livraisons à contrôler',
  menuFactures: 'Factures',
  menuArgent: 'Argent',
  menuStock: 'Stock de carburant',
  // Société sans dépôt: son stock est ce qui est chargé et pas encore livré.
  menuStockSansDepot: 'Carburant en route',
  menuDocuments: 'Documents',
  menuRapports: 'Rapports',
  menuVueDirection: 'Vue d’ensemble de la direction',
  menuExports: 'Exports',
  menuPlus: 'Plus',
  menuAlertes: 'Alertes',
  menuTarifs: 'Tarifs',
  menuZones: 'Zones géographiques',
  menuSocietes: 'Sociétés',
  menuUtilisateurs: 'Utilisateurs',
  menuBlocages: 'Blocages',
  menuAccesClients: 'Accès clients',
  menuFournisseursGps: 'Fournisseurs GPS',
  /** Titre de la feuille ouverte par « Plus » sur téléphone. */
  menuTousLesEcrans: 'Tous les écrans',

  /* ---------------------------------------------------------------- */
  /* Accueil: le bandeau « À ce jour »                                  */
  /* ---------------------------------------------------------------- */

  aCeJour: 'À ce jour',
  camionsDisponibles: 'Camions disponibles',
  enRoute: 'En route',
  aLivrer: 'À livrer',
  aEncaisser: 'À encaisser',
  aucunCamionDisponible: 'Aucun camion libre',
  aucunCamionEnRoute: 'Aucun camion en route',
  rienALivrer: 'Rien à livrer',
  rienAEncaisser: 'Rien à encaisser',

  /* ---------------------------------------------------------------- */
  /* Argent et bilan: lecture d'un coup d'œil                          */
  /* ---------------------------------------------------------------- */

  /** Les écrans d'argent se lisent par questions, pas par vocabulaire
   *  comptable: « Résultat », « Trésorerie », « Créances », « Dettes »
   *  deviennent ce que l'exploitant se demande vraiment. */
  questionGagne: 'Qu’est-ce que j’ai gagné ?',
  questionOuArgent: 'Où est mon argent ?',
  questionQuiDoit: 'Qui doit me payer ?',
  questionBientot: 'Qu’est-ce qui arrive bientôt ?',
  questionAFaire: 'Qu’est-ce qu’il reste à faire ?',

  resultatMois: 'Résultat du mois',
  resultatPeriode: 'Résultat de la période',
  /** Ex-« Trésorerie ». */
  argentDisponible: 'Argent disponible',
  argentCaisseBanque: 'En caisse et en banque',
  /** Ex-« Créances clients » / « Encours clients ». */
  clientsVousDoivent: 'Les clients vous doivent',
  /** Ex-« Dettes » (fournisseurs et propriétaires). */
  vousDevez: 'Vous devez',
  /** Les tuiles de « Où est mon argent ? ». */
  chezClients: 'Chez les clients',
  aPayerFournisseurs: 'À payer aux fournisseurs',
  aPayerProprietaires: 'À payer aux propriétaires',
  recettes: 'Recettes',
  depenses: 'Dépenses',
  benefice: 'Bénéfice',
  perte: 'Perte',
  equilibre: 'À l’équilibre',
  autresChiffres: 'Autres chiffres du mois',
};

export default LEXIQUE;
